import { useMemo } from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  formatDateForDisplay,
  isDateWithinRange,
  type OrderRecord,
} from "@/lib/utils";

// ----------------------------------------------------------------------------

interface RecentOrdersListProps {
  className?: string;
  startDate: string;
  endDate: string;
  orders: OrderRecord[];
  isLoading?: boolean;
  errorMessage?: string | null;
}

export function RecentOrdersList(props: RecentOrdersListProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    className,
    startDate,
    endDate,
    orders,
    isLoading = false,
    errorMessage = null,
  } = props;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const filteredOrders = useMemo(
    () =>
      orders.filter((order) =>
        isDateWithinRange(order.date, startDate, endDate),
      ),
    [orders, endDate, startDate],
  );

  const recentOrders = useMemo(
    () =>
      [...filteredOrders].sort((leftOrder, rightOrder) => {
        const leftTimestamp = Date.parse(leftOrder.date);
        const rightTimestamp = Date.parse(rightOrder.date);

        return rightTimestamp - leftTimestamp;
      }),
    [filteredOrders],
  );

  // -------------------------------------
  // Component functions
  // -------------------------------------

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  return (
    <Card className={`shadow-sm ${className || ""}`}>
      <CardHeader>
        <CardTitle className="text-lg font-bold">Ultimi ordini</CardTitle>
        <CardDescription className="text-xs">
          {recentOrders.length} ordini nel range selezionato
        </CardDescription>
      </CardHeader>
      <CardContent>
        {errorMessage && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {errorMessage}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-6 py-1">
            {Array.from({ length: 5 }, (_, index) => (
              <div
                key={`recent-order-skeleton-${index}`}
                className="flex items-center justify-between gap-4"
              >
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {recentOrders.slice(0, 5).map((order) => (
              <div key={order.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {order.customerName}
                  </p>
                  <p className="text-xs text-gray-500">{order.email}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDateForDisplay(order.date)}
                  </p>
                </div>
                <div className="font-bold text-sm text-gray-900">
                  {order.total}
                </div>
              </div>
            ))}
            {recentOrders.length === 0 && (
              <p className="text-sm text-gray-500 py-4">
                Nessun ordine nel range selezionato.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
